import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, useCallback, useEffect, useState } from 'react'
import { AppComponentProps } from '../../../../types/mfc'
import { openMessage } from '../../../actions/message'
import { goToMFCProfile } from '../../../actions/nav'
import { openRoom } from '../../../actions/room'
import { addFriend, ignoreUser, removeFriend } from '../../../actions/user'
import {
    openAuthenticationDrawer,
    openReportUserDrawer,
} from '../../../app-state/modals/state.drawer'
import { isLounge } from '../../../mfcLogic/userProperties/user'
import i18n from '../../../utilities/i18n'
import { AppStateType, useAppStateSelector } from '../../Hooks/useAppState'
import { useUserExists, useUserSelector } from '../../Hooks/useUser'
import { SocialIcon, SocialPlatforms } from '../../Icons/SocialIcon'
import FlexGrid from '../../common/FlexGrid'
// import { StyledContainerExtraSmall } from '../../common/Layout'
import { isIOSPWA } from '../../../utilities/browser'
import { TransitionTypes } from '../../../utilities/transitions'
import Button from '../../Buttons/Button'
import NameAvatar from '../../common/NameAvatar'
import Share from '../../common/Share'
import { getThemeSpacing } from '../../styles/appTheme'
import APP_BREAKPOINTS, {
    maxWindowWidth,
    minWindowWidth,
} from '../../styles/breakpoints'
import { palette } from '../../styles/colors'
import '../Modals/FullscreenFloater'
import Tags from '../Tags'
import YouMayLike from '../YouMayLike/embedded/EmbeddedRecommendations'
import Actions from './Actions'
import Table from './Table'
import Title from './Title'

const StyledRootDiv = styled('div')({
    flexDirection: 'column',
    display: 'flex',
    flex: 1,
    paddingLeft: 24,
    paddingRight: 24,
    [minWindowWidth(APP_BREAKPOINTS.sm)]: {
        maxWidth: 600,
    },
})
const enterChatButtonStyles = css({
    fontWeight: 600,
    width: '100%',
    marginTop: getThemeSpacing(1.5),
    marginBottom: getThemeSpacing(1),
    [maxWindowWidth(APP_BREAKPOINTS.sm)]: {
        fontSize: 12.5,
        lineHeight: 1.96,
    },
})

const drawerEnterChatContainer = css({
    marginTop: 12,
    width: '100%',
})

const StyledGrid = styled('div')({
    display: 'grid',
})

const tagsGridStyles = {
    // styled(FlexGrid)({
    marginBottom: getThemeSpacing(2),
}

const iFrameContainerGridStyles = {
    // styled(FlexGrid)({
    padding: '0 !important',
}

const socialNoContainerStyles = css({
    position: 'absolute',
    top: -2,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 43,
})
const socialNonEmbeddedStyles = css({
    top: 16,
})
const socialContainerStyles = css({
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
})

const StyledSocialIconWrapper = styled('div')({
    marginLeft: getThemeSpacing(0.5),
})

const attributesGridStyles = {
    position: 'relative',
} as const

const nonModelNameStyles = css({
    borderBottom: `1px solid ${palette.drawerDivider}`,
    '& h6': {
        fontSize: '1.2rem',
    },
})

interface ProfileProps extends Partial<AppComponentProps> {
    slugOrId: string | number
    drawer?: boolean
    embedded?: boolean
    showRecommendations?: boolean
}

// This is for the profile page route component
const EmptyProfile = memo(() => {
    return null
})

export const Profile = memo((props: ProfileProps) => {
    const { slugOrId, drawer, embedded, showRecommendations } = props

    const [iframeHeight, setIframeHeight] = useState(0)
    const isAuthenticated = useAppStateSelector(
        AppStateType.APP,
        'authenticated',
    )
    const darkMode = useAppStateSelector(AppStateType.APP, 'darkMode')
    const isMobile = useAppStateSelector(AppStateType.APP, 'isMobile')

    const isMobileDrawer = isMobile && drawer

    useUserExists(slugOrId, { loadExtras: true })
    const id = useUserSelector(slugOrId, 'id')
    const shareAlbums = useUserSelector(slugOrId, 'shareAlbums') ?? 0
    const shareThings = useUserSelector(slugOrId, 'shareThings') ?? 0
    const shareTipmenus = useUserSelector(slugOrId, 'shareTipmenus') ?? 0
    const shareRecordings = useUserSelector(slugOrId, 'shareRecordings') ?? 0
    const attributes = useUserSelector(slugOrId, 'attributes') ?? []
    const isHidden = useUserSelector(slugOrId, 'isHidden')
    const isIgnored = useUserSelector(slugOrId, 'isIgnored')
    const hasNote = useUserSelector(slugOrId, 'hasNote')
    // const hasWhiteboard = useUserSelector(slugOrId, 'hasWhiteboard')
    const isPublicShow = useUserSelector(slugOrId, 'isPublicShow')
    const instagram = useUserSelector(slugOrId, 'instagram')
    const twitter = useUserSelector(slugOrId, 'twitter')
    const isBookmark = useUserSelector(slugOrId, 'isBookmark')
    const isFriend = useUserSelector(slugOrId, 'isFriend')
    const isOffline = useUserSelector(slugOrId, 'isOffline')
    const isModel = useUserSelector(slugOrId, 'isModel')
    const tags = useUserSelector(slugOrId, 'tags')
    const slug = useUserSelector(slugOrId, 'slug')
    const name = useUserSelector(slugOrId, 'name')
    const lounge = isLounge(id)

    const hasShare =
        shareAlbums > 0 ||
        shareRecordings > 0 ||
        shareThings > 0 ||
        shareTipmenus > 0

    //
    // Marc: Leo has asked to remove this section for now, but it might come
    // back.
    //
    // const shouldShowFriendsRatings = false
    // admirers > 0 || favorites.length > 0 || profileFriends > 0 || rating > 0
    const shareProfileUrl = `https://share.myfreecams.com/embed/u/${slug}?color_scheme_dark=${
        darkMode || false
    }`

    useEffect(() => {
        const onMessage = (event) => {
            const { data } = event
            if (typeof data !== 'object' || !data.height) {
                // console.warn(
                //     'Expected message to contain profile iframe dimensions',
                // )
                return
            }
            const { height } = data
            setIframeHeight(height)
        }

        window.addEventListener('message', onMessage)

        return () => window.removeEventListener('message', onMessage)
    }, [])

    const onFriend = useCallback(() => {
        if (!isAuthenticated) {
            openAuthenticationDrawer()
            return
        }
        if (isFriend) removeFriend(slug)
        else addFriend(slug)
    }, [isAuthenticated, isFriend, slug])

    const onIgnore = useCallback(() => {
        if (!isAuthenticated) {
            openAuthenticationDrawer()
            return
        }
        ignoreUser(slug)
    }, [isAuthenticated, slug])

    const onOpenMessage = useCallback(() => {
        if (!isAuthenticated) {
            openAuthenticationDrawer()
            return
        }
        openMessage(slug, drawer ? TransitionTypes.MODAL : TransitionTypes.ROOM)
    }, [isAuthenticated, slug, drawer])

    const onOpenRoom = useCallback(() => {
        openRoom(slug, {
            transitionSource: drawer
                ? TransitionTypes.MODAL
                : TransitionTypes.ROOM,
        })
    }, [slug, drawer])

    const onReportMenu = useCallback(() => {
        if (!isAuthenticated) {
            openAuthenticationDrawer()
            return
        }
        openReportUserDrawer({ id, name })
    }, [isAuthenticated, id, name])

    const onOpenMFCProfile = useCallback(() => {
        goToMFCProfile(slug)
    }, [slug])

    /**
     * Get Social media profile information
     * @param container
     * @returns
     */
    const getSocial = useCallback(
        (container?: boolean) => {
            if (!twitter && !instagram && !hasShare) {
                return null
            }

            const social = (
                <div
                    data-testid="Profile__SocialGrid"
                    css={[
                        container
                            ? socialContainerStyles
                            : socialNoContainerStyles,
                        !embedded && socialNonEmbeddedStyles,
                    ]}
                >
                    {!!twitter && (
                        <StyledSocialIconWrapper>
                            <SocialIcon
                                url={`https://twitter.com/${twitter}`}
                                platform={SocialPlatforms.twitter}
                                dimension={26}
                            />
                        </StyledSocialIconWrapper>
                    )}
                    {!!instagram && (
                        <StyledSocialIconWrapper>
                            <SocialIcon
                                url={`https://www.instagram.com/${instagram}`}
                                platform={SocialPlatforms.instagram}
                                dimension={26}
                            />
                        </StyledSocialIconWrapper>
                    )}
                    {hasShare && (
                        <StyledSocialIconWrapper>
                            <Share
                                albums={shareAlbums}
                                recordings={shareRecordings}
                                width={100}
                                slug={slug}
                            />
                        </StyledSocialIconWrapper>
                    )}
                </div>
            )

            /* if we do not need a container */
            if (!container) {
                return social
            }

            /* if we need a container */
            return (
                <StyledGrid data-testid="Profile__SocialGrid">
                    {social}
                </StyledGrid>
            )
        },
        [
            slug,
            embedded,
            twitter,
            instagram,
            hasShare,
            shareAlbums,
            shareRecordings,
        ],
    )
    /** no user and no slug */
    if (!id || !slug) {
        return null
    }

    /* if member */
    if (!isModel) {
        return (
            <StyledRootDiv data-testid="Profile__RootSSROrNonModel">
                {/* <StyledContainerExtraSmall data-testid="Profile__Actions"> */}
                <NameAvatar
                    offline={isOffline}
                    name={name}
                    id={id}
                    addBoxShadow={true}
                    styleOverrides={nonModelNameStyles}
                />
                <Actions
                    onFriend={onFriend}
                    onOpenMessage={onOpenMessage}
                    onIgnore={onIgnore}
                    onReportMenu={onReportMenu}
                    reportMenu={true}
                    isFriend={isFriend}
                    isIgnored={isIgnored}
                    hasNote={hasNote}
                    isHidden={isHidden}
                    isBookmark={isBookmark}
                    hasWhiteboard={false}
                    isPublicShow={isPublicShow}
                    isOffline={isOffline}
                    isModel={false}
                    header={false}
                    slug={slug}
                    name={name}
                    id={id}
                    mfcProfile
                />
                {/* </StyledContainerExtraSmall> */}
            </StyledRootDiv>
        )
    }

    const sectionStyles = {
        width: '100%',
        paddingTop: 0,
        overflow: 'auto',
        // maxWidth: !embedded ? 600 : '100%',
        margin: !embedded && !drawer ? '0 auto' : 0,
        paddingLeft: !embedded ? getThemeSpacing(2) : getThemeSpacing(1),
        paddingRight: !embedded ? getThemeSpacing(2) : getThemeSpacing(1),
    }

    return (
        <section className="no-scrollbars" style={sectionStyles}>
            <div data-testid="BodyGridContainer">
                {lounge && (
                    <NameAvatar showAvatar={false} name={name} id={id} />
                )}
                {drawer && (
                    <Button
                        data-testid="Button__EnterChat"
                        onClick={onOpenRoom}
                        size="medium"
                        css={drawerEnterChatContainer}
                    >
                        {i18n.t('menu.enterChat')}
                    </Button>
                )}
                {!!attributes.length && (
                    <FlexGrid
                        data-testid="Table__Attributes"
                        style={attributesGridStyles}
                    >
                        {getSocial()}
                        <Title
                            bottom={!embedded ? 16 : 8}
                            top={!embedded ? 26 : 8}
                        >
                            About Me
                        </Title>
                        <Table rows={attributes} />
                    </FlexGrid>
                )}
                {!attributes.length && getSocial(true)}
                {/* {shouldShowFriendsRatings && (
                    <FriendsRatings
                        admirers={admirers}
                        embedded={embedded}
                        favorites={favorites}
                        profileFriends={profileFriends}
                        rating={rating}
                    />
                )} */}
                {!embedded && !isMobileDrawer && (
                    <Button
                        data-testid="Button__FullMFCProfile"
                        onClick={onOpenMFCProfile}
                        size="medium"
                        clean
                        border
                        css={enterChatButtonStyles}
                    >
                        {i18n.t('menu.mfcProfile')}
                    </Button>
                )}
                {hasShare && (
                    <FlexGrid style={iFrameContainerGridStyles}>
                        <iframe
                            src={shareProfileUrl}
                            height={iframeHeight}
                            allowFullScreen
                            frameBorder="0"
                            scrolling="no"
                            width="100%"
                        />
                    </FlexGrid>
                )}
                <FlexGrid style={tagsGridStyles} data-testid="Grid__Tags">
                    <Tags tags={!!tags && tags.split('  ')} />
                </FlexGrid>
                {showRecommendations && (
                    <div
                        css={{
                            [minWindowWidth(APP_BREAKPOINTS.s)]: {
                                paddingBottom: getThemeSpacing(2),
                            },
                            [minWindowWidth(APP_BREAKPOINTS.md)]: {
                                paddingBottom: getThemeSpacing(3.5),
                            },
                            ...(isIOSPWA
                                ? {
                                      marginBottom: getThemeSpacing(5),
                                  }
                                : null),
                        }}
                    >
                        <YouMayLike id={id} inRoomOrProfile />
                    </div>
                )}
            </div>
        </section>
    )
})

export default EmptyProfile
